/**
 * PUT a file to a signed Storage url with upload progress (spec 2026-09-24,
 * Part A). fetch cannot report upload progress, and a 500 MB upload takes
 * minutes: a spinner with no number reads as stuck.
 *
 * Resolves with the status and the raw body for ANY answer, so the caller can
 * read a refusal structurally; rejects only when the request never completed
 * (a network error, an abort).
 *
 * `signal` cancels it (ruling I3): a person who removes a row mid-upload must
 * not have the file land in Files anyway. A cancel rejects with an Error named
 * "AbortError", so the caller can tell it from a failure.
 */
export interface PutResult {
  status: number;
  body: string;
}

function cancelled(): Error {
  const err = new Error("Upload cancelled");
  err.name = "AbortError";
  return err;
}

export function putWithProgress(
  url: string,
  body: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal
): Promise<PutResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const onCancel = () => {
      reject(cancelled());
      xhr.abort();
    };
    const release = () => signal?.removeEventListener("abort", onCancel);
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      release();
      resolve({ status: xhr.status, body: xhr.responseText ?? "" });
    };
    xhr.onerror = () => {
      release();
      reject(new Error("Upload failed"));
    };
    xhr.onabort = () => {
      release();
      reject(new Error("Upload failed"));
    };
    if (signal?.aborted) {
      onCancel();
      return;
    }
    signal?.addEventListener("abort", onCancel, { once: true });
    xhr.send(body);
  });
}
